import { Controller } from "@hotwired/stimulus"

const POLL_INTERVAL_MS = 10000;

export default class extends Controller {
  static targets = [
    "date",
    "settlementAccount",
    "accountDetails",
    "dailySummaries",
    "report",
    "adjustments",
    "adjustmentErrors"
  ]
  static values = {
    url: String,
    recalculateUrl: String,
    rerunChecksUrl: String,
    addAdjustmentUrl: String,
    today: String
  }

  connect() {
    this.schedulePoll();
  }

  disconnect() {
    this.cancelPoll();
  }

  refresh() {
    const url = new URL(this.urlValue, window.location.origin);
    url.searchParams.set("date", this.dateTarget.value);
    url.searchParams.set("settlement_account", this.settlementAccountTarget.value);

    Rails.ajax({
      type: "get",
      url: url.toString(),
      success: (data) => this.applyReport(data),
      error: (data) => {
        console.error(data);
      }
    });
  }

  recalculate(event) {
    const message = "Recalculate the totals for this day? This overwrites the stored totals and " +
      "recalculates the variance for this and every later day.";
    if (!confirm(message)) return;

    event.currentTarget.disabled = true;
    this.postAndApply(this.recalculateUrlValue);
  }

  rerunChecks(event) {
    const message = "Re-run all checks for this day? This replaces each check's existing result.";
    if (!confirm(message)) return;

    event.currentTarget.disabled = true;
    this.postAndApply(this.rerunChecksUrlValue);
  }

  addAdjustment(event) {
    event.preventDefault();
    const form = event.currentTarget;
    const submit = form.querySelector("[type='submit']");
    submit.disabled = true;

    Rails.ajax({
      type: "post",
      url: this.addAdjustmentUrlValue,
      data: new URLSearchParams({
        ...this.selectionParams(),
        amount: form.elements.amount.value,
        description: form.elements.description.value
      }).toString(),
      success: (data) => {
        form.reset();
        submit.disabled = false;
        this.adjustmentErrorsTarget.classList.add("d-none");
        this.applyReport(data);
      },
      error: (data) => {
        submit.disabled = false;
        this.showAdjustmentErrors(data);
        this.refresh();
      }
    });
  }

  removeAdjustment(event) {
    const message = "Remove this adjustment? Variances will be recalculated starting from the adjustment's date.";
    if (!confirm(message)) return;

    const button = event.currentTarget;
    button.disabled = true;

    Rails.ajax({
      type: "delete",
      url: button.dataset.url,
      data: new URLSearchParams(this.selectionParams()).toString(),
      success: (data) => {
        this.adjustmentErrorsTarget.classList.add("d-none");
        this.applyReport(data);
      },
      error: (data) => {
        this.showAdjustmentErrors(data);
        this.refresh();
      }
    });
  }

  showAdjustmentErrors(data) {
    if (!data?.errors) {
      console.error(data);
      return;
    }

    this.adjustmentErrorsTarget.textContent = data.errors.join(". ");
    this.adjustmentErrorsTarget.classList.remove("d-none");
  }

  postAndApply(url) {
    Rails.ajax({
      type: "post",
      url,
      data: new URLSearchParams(this.selectionParams()).toString(),
      success: (data) => this.applyReport(data),
      error: (data) => {
        console.error(data);
        this.refresh();
      }
    });
  }

  selectionParams() {
    return { date: this.dateTarget.value, settlement_account: this.settlementAccountTarget.value };
  }

  applyReport(data) {
    this.accountDetailsTarget.innerHTML = data.account_details_html;
    this.dailySummariesTarget.innerHTML = data.daily_summaries_html;
    this.reportTarget.innerHTML = data.report_html;
    this.adjustmentsTarget.innerHTML = data.adjustments_html;
    this.schedulePoll();
  }

  schedulePoll() {
    this.cancelPoll();
    if (!this.reportTarget.querySelector("[data-recalculating='true'], [data-checks-running='true']")) return;

    this.pollTimer = setTimeout(() => this.refresh(), POLL_INTERVAL_MS);
  }

  cancelPoll() {
    if (!this.pollTimer) return;

    clearTimeout(this.pollTimer);
    this.pollTimer = null;
  }

  pageEarlier() {
    this.shiftDate(-1);
  }

  pageLater() {
    this.shiftDate(1);
  }

  jumpToToday() {
    this.selectDate(this.parseDate(this.todayValue));
  }

  selectRow(event) {
    this.selectDate(this.parseDate(event.currentTarget.dataset.date));
  }

  shiftDate(days) {
    const picker = this.dateTarget._flatpickr;
    const current = picker?.selectedDates[0] ?? this.parseDate(this.dateTarget.value);
    const next = new Date(current);
    next.setDate(next.getDate() + days);
    this.selectDate(next);
  }

  selectDate(date) {
    const picker = this.dateTarget._flatpickr;

    if (picker) {
      picker.setDate(date, true);
    } else {
      this.dateTarget.value = date.toISOString().slice(0, 10);
      this.dateTarget.dispatchEvent(new Event("change"));
    }
  }

  parseDate(isoDate) {
    return new Date(`${isoDate}T00:00:00`);
  }
}
